import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BadgeCheck, Crown, Loader2, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  { id: 'tier1', name: 'Tier 1 · Treasury Officer', price: 499, icon: ShieldCheck, tone: 'emerald',
    perks: ['Treasury Officer; Banker title', 'Credit line eligibility', '$250,000 daily limit', 'Treasury checks and BETH USD minting desk'] },
  { id: 'tier2', name: 'Tier 2 · Hedge Fund', price: 999, icon: Crown, tone: 'amber',
    perks: ['Everything in Tier 1', '$50M BETH USD credit line', 'Project funding deals', 'Credit cash-out to your bank'] },
];
const TONES = { emerald: 'border-emerald-500/40 text-emerald-300 bg-emerald-600 hover:bg-emerald-500', amber: 'border-amber-500/40 text-amber-300 bg-amber-600 hover:bg-amber-500' };

const TierCard = ({ t, action }) => {
  const [border, text, ...btn] = TONES[t.tone].split(' ');
  const Icon = t.icon;
  return (
    <div className={`bg-slate-900 rounded-2xl p-6 border ${border} flex flex-col gap-4`} data-testid={`tier-card-${t.id}`}>
      <div className="flex items-center gap-2"><Icon className={`w-6 h-6 ${text}`} /><h3 className="text-lg font-bold text-white">{t.name}</h3></div>
      <p className="text-3xl font-bold text-white" data-testid={`tier-price-${t.id}`}>${t.price}<span className="text-sm text-slate-400 font-normal">/month</span></p>
      <ul className="space-y-1.5 text-sm text-slate-300 flex-1">{t.perks.map(p => <li key={p} className="flex gap-2"><BadgeCheck className={`w-4 h-4 mt-0.5 shrink-0 ${text}`} />{p}</li>)}</ul>
      {action(t, btn.join(' '))}
    </div>
  );
};

export const MembershipTiers = () => {
  const navigate = useNavigate();
  return (
    <section id="membership" className="py-20 px-4" data-testid="landing-membership-section">
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="text-center space-y-2">
          <p className="text-amber-400 text-xs uppercase tracking-[0.3em]">Membership</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white">Tier 1 &amp; Tier 2 subscriptions</h2>
          <p className="text-slate-400">Monthly plans, cancel any time. Tier 2 requires an active Tier 1 membership.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {TIERS.map(t => <TierCard key={t.id} t={t} action={(tier, cls) => (
            <button onClick={() => navigate('/pas/signup?next=membership')} className={`py-3 rounded-xl font-semibold text-white transition-colors ${cls}`} data-testid={`landing-subscribe-${tier.id}`}>Join {tier.id === 'tier1' ? 'Tier 1' : 'Tier 2'}</button>
          )} />)}
        </div>
      </div>
    </section>
  );
};

export const MyMembership = ({ token }) => {
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState('');
  useEffect(() => {
    axios.get(`${API}/pas-bank-statement/membership`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setM(r.data)).catch(() => {});
  }, [token]);
  const subscribe = async (id) => {
    setBusy(id);
    try {
      const url = id === 'tier1' ? `${API}/pas-banking/subscription/checkout` : `${API}/pas-credit-cashout/subscribe`;
      window.location.href = (await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } })).data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start checkout'); setBusy(''); }
  };
  if (!m) return null;
  const action = (t, cls) => {
    const s = m[t.id];
    if (s.active) return <p className={`py-3 text-center rounded-xl font-semibold border ${TONES[t.tone].split(' ').slice(0, 2).join(' ')}`} data-testid={`membership-active-${t.id}`}>{m.is_owner ? 'Included (owner)' : s.waived ? 'Active (fee waived)' : s.admin_granted ? 'Active (granted)' : 'Active'}</p>;
    const locked = t.id === 'tier2' && !m.tier1.active;
    const label = locked ? 'Join Tier 1 first' : t.id === 'tier2' ? `Upgrade to Tier 2 · $${t.price}/mo` : `Subscribe · $${t.price}/mo`;
    return (
      <button disabled={locked || !!busy} onClick={() => subscribe(t.id)} className={`py-3 rounded-xl font-semibold text-white transition-colors disabled:opacity-40 flex items-center justify-center gap-2 ${cls}`} data-testid={`membership-subscribe-${t.id}`}>
        {busy === t.id && <Loader2 className="w-4 h-4 animate-spin" />}{label}
      </button>
    );
  };
  return (
    <div className="mb-8" data-testid="my-membership">
      <h2 className="text-lg font-semibold text-white mb-3">My membership</h2>
      <div className="grid md:grid-cols-2 gap-4">{TIERS.map(t => <TierCard key={t.id} t={t} action={action} />)}</div>
    </div>
  );
};
